import type { IntegrationCheckView, ProjectBrief, Task } from '@dyspatch/core';
import { GuardedButton } from '@/components/common/bits';
import { Badge } from '@/components/ui/badge';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import { labelForKind } from '@/lib/event-kinds';
import { duration, minutesSince } from '@/lib/format';
import { checkReadiness, runningCheck } from '@/lib/integration';

/**
 * Does this job's work hold together? (Issue #26.)
 *
 * Every pull request in the job was gated on its own, against a tree carrying
 * that step's change and nothing else from the job, so the combination was never
 * tested: five green PRs can still break the base branch together. This region
 * is the answer to that, and it sits directly above the merge order because
 * that is the moment a person needs it - they are about to start clicking
 * Merge, and this is the only thing on the page that has read the combination.
 *
 * What it will not do is pretend. A verdict goes stale the instant anything in
 * the job merges, and a stale green is more dangerous than no verdict at all, so
 * staleness is said first and in the badge rather than in a footnote.
 */

const VERDICT: Record<
  IntegrationCheckView['verdict'],
  { label: string; variant: 'success' | 'destructive' | 'warning' }
> = {
  green: { label: 'Holds together', variant: 'success' },
  red: { label: 'Gates failed', variant: 'destructive' },
  conflict: { label: 'Branches conflict', variant: 'warning' },
};

export function IntegrationCheck({
  brief,
  tasks,
  check,
  mayShape,
}: {
  brief: ProjectBrief;
  tasks: readonly Task[];
  check: IntegrationCheckView | null;
  /** The person may spend somebody's laptop on this job: they filed it, or they are an admin. */
  mayShape: boolean;
}) {
  const running = runningCheck(tasks);
  const readiness = checkReadiness(tasks);
  const run = useHubAction(() => api.checkJob(brief.id), {
    success: 'Check queued. A worker picks it up on the next sweep.',
  });

  // Before Go there is nothing combined to test, and after the job closes there
  // is nothing left to decide. Drawing an empty region on either would spend a
  // heading on a question nobody is asking yet.
  if (brief.status !== 'approved') return null;

  const refusal = !mayShape
    ? `"${brief.title}" was filed by ${brief.submittedBy}; only they or an admin can check it`
    : running
      ? `${running.key} is already ${labelForKind(running.status).toLowerCase()}`
      : readiness.reason;

  return (
    <Section>
      <SectionHead
        title="Integration check"
        meta={
          check
            ? `${check.branches.length} branch${check.branches.length === 1 ? '' : 'es'} · ${duration(minutesSince(check.checkedAt))} ago`
            : undefined
        }
        actions={
          <GuardedButton
            variant={check ? 'outline' : 'subtle'}
            size="xs"
            refusal={refusal}
            disabled={run.busy}
            onClick={() => run.run()}
          >
            {run.busy ? 'Queueing…' : check ? 'Check again' : 'Run the check'}
          </GuardedButton>
        }
      />
      <Surface>
        <div className="px-gutter py-3 text-sm">
          {check ? (
            <Verdict check={check} />
          ) : running ? (
            <p className="text-fg-2">
              {running.key} is {labelForKind(running.status).toLowerCase()}
              {running.assignedWorkerName ? ` on ${running.assignedWorkerName}` : ''}. It merges
              every open branch in this job into one scratch branch and runs the repo's own gates on
              the result.
            </p>
          ) : (
            <p className="text-fg-2">
              Each pull request here was tested on its own. Nothing has tested them together yet.
              {refusal ? ` ${sentence(refusal)}` : ''}
            </p>
          )}
        </div>
      </Surface>
    </Section>
  );
}

function Verdict({ check }: { check: IntegrationCheckView }) {
  const tone = VERDICT[check.verdict];
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={check.stale ? 'secondary' : tone.variant}>{tone.label}</Badge>
        {check.stale ? <Badge variant="warning">Stale</Badge> : null}
        <span className="text-fg-3 text-xs">
          {check.taskKey} on {check.workerName ?? 'a worker'}, asked for by {check.requestedBy}
        </span>
      </div>

      {check.stale && check.staleReason ? (
        // Said before the verdict itself, and in the reader's own terms: the
        // danger is a person reading a green that was true an hour ago.
        <p className="text-warn mt-2 text-sm">
          This verdict no longer describes the job: {check.staleReason}. Run it again before you
          trust it.
        </p>
      ) : null}

      {check.summary ? <p className="text-fg-2 mt-2 whitespace-pre-wrap">{check.summary}</p> : null}

      {check.conflicts.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {check.conflicts.map((conflict) => (
            <li key={`${conflict.repo}:${conflict.branch}`} className="min-w-0">
              <div className="text-sm">
                <span className="font-medium">{conflict.taskKey}</span> conflicts with{' '}
                <span className="font-medium">{conflict.withTaskKey ?? conflict.withBranch}</span>
                {conflict.withTaskKey ? '' : ', the base branch'}.
              </div>
              <div className="text-fg-3 mt-0.5 font-mono text-2xs break-all">
                {conflict.branch} × {conflict.withBranch}
              </div>
              {conflict.paths.length > 0 ? (
                <div className="text-fg-3 mt-0.5 text-xs">
                  Conflicted: {conflict.paths.join(', ')}
                </div>
              ) : null}
              <details className="text-2xs mt-1">
                <summary className="text-fg-3 hover:text-fg-2 cursor-pointer list-none select-none transition-colors duration-100">
                  What git said →
                </summary>
                <pre className="bg-sunken/60 text-fg-3 mt-1 max-h-64 overflow-auto rounded-sm p-2 font-mono whitespace-pre-wrap">
                  {conflict.detail}
                </pre>
              </details>
            </li>
          ))}
        </ul>
      ) : null}

      {check.gates.some((gate) => !gate.ok) ? (
        <ul className="mt-3 space-y-2">
          {check.gates
            .filter((gate) => !gate.ok)
            .map((gate) => (
              <li key={`${gate.repo}:${gate.name}`} className="min-w-0">
                <div className="text-bad font-mono text-xs break-all">
                  {gate.repo}: {gate.name}
                </div>
                {gate.output ? (
                  <details className="text-2xs mt-1">
                    <summary className="text-fg-3 hover:text-fg-2 cursor-pointer list-none select-none transition-colors duration-100">
                      What the gate printed →
                    </summary>
                    <pre className="bg-sunken/60 text-fg-3 mt-1 max-h-64 overflow-auto rounded-sm p-2 font-mono whitespace-pre-wrap">
                      {gate.output}
                    </pre>
                  </details>
                ) : null}
              </li>
            ))}
        </ul>
      ) : null}

      <div className="text-fg-3 mt-3 text-xs">
        Tested {check.branches.map((entry) => entry.taskKey).join(', ')} on{' '}
        {check.bases.map((base) => `${base.repo}@${base.baseBranch}`).join(', ') || 'the base'}.
      </div>
    </div>
  );
}

/** A refusal reason reads as its own sentence when it follows one. */
function sentence(text: string): string {
  const trimmed = text.trim();
  if (trimmed === '') return '';
  const capitalised = trimmed[0]!.toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
}
